import { useParams } from 'react-router'

export const SUPPORTED_LANGUAGES = ['de'] as const
// Languages we used to serve — kept so we can redirect legacy URLs to de.
export const LEGACY_LANGUAGES = ['fr', 'en'] as const
export const MARKETING_PATHS = [
	'',
	'/about',
	'/contact',
	'/terms',
	'/privacy',
	'/dpa',
	'/sla',
	'/imprint',
	'/websites',
	'/cloudron',
	'/nextcloud',
	'/coaching',
	'/blog',
] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]
export const DEFAULT_LANGUAGE: SupportedLanguage = 'de'

export function isSupportedLanguage(lang: string): lang is SupportedLanguage {
	return (SUPPORTED_LANGUAGES as readonly string[]).includes(lang)
}

export function isLegacyLanguage(lang: string): boolean {
	return (LEGACY_LANGUAGES as readonly string[]).includes(lang)
}

/**
 * Returns the current language from the URL $lang param.
 * Falls back to 'de' if not present.
 */
export function useLang(): SupportedLanguage {
	const params = useParams<{ lang?: string }>()
	if (params.lang && isSupportedLanguage(params.lang)) {
		return params.lang
	}
	return DEFAULT_LANGUAGE
}

/**
 * Returns a function that prepends the current lang prefix to a path.
 *
 * Usage:
 *   const localePath = useLocalizedPath()
 *   <Link to={localePath('/about')}>About</Link>
 *   // → /de/about, /fr/about, etc.
 */
export function useLocalizedPath() {
	const lang = useLang()
	return (path: string) => {
		if (path === '/' || path === '') return `/${lang}`
		const cleanPath = path.startsWith('/') ? path : `/${path}`
		return `/${lang}${cleanPath}`
	}
}

/**
 * Build a localized path with a given language (for server-side use).
 */
export function localizedPath(lang: string, path: string): string {
	if (path === '/' || path === '') return `/${lang}`
	const cleanPath = path.startsWith('/') ? path : `/${path}`
	return `/${lang}${cleanPath}`
}

/**
 * Always returns the default language — the site is served in de only,
 * so Accept-Language is intentionally ignored.
 */
export function detectLanguageFromHeader(
	_acceptLanguage: string | null,
): SupportedLanguage {
	return DEFAULT_LANGUAGE
}

/**
 * Strip the language prefix from a URL pathname.
 * e.g. "/de/about" → "/about", "/fr" → ""
 */
export function stripLangPrefix(pathname: string): string {
	const match = pathname.match(/^\/(?:de|fr|en)(\/.*)?$/)
	return match ? (match[1] ?? '') : pathname
}

/**
 * Whether a pathname is the shop or one of its pages, with or without a
 * language prefix. "/shop/x" and "/de/shop" match, "/shopping" does not.
 */
export function isShopPath(pathname: string): boolean {
	const path = stripLangPrefix(pathname)
	return path === '/shop' || path.startsWith('/shop/')
}

/**
 * Generate hreflang <link> descriptors for all supported languages.
 * x-default points to the primary language (de).
 */
export function getHreflangLinks(
	pathname: string,
	siteUrl: string,
): Array<{ tagName: 'link'; rel: string; hrefLang: string; href: string }> {
	const pathWithoutLang = stripLangPrefix(pathname)
	const base = siteUrl.replace(/\/$/, '')

	const links: Array<{
		tagName: 'link'
		rel: string
		hrefLang: string
		href: string
	}> = SUPPORTED_LANGUAGES.map((lang) => ({
		tagName: 'link' as const,
		rel: 'alternate',
		hrefLang: lang,
		href: `${base}/${lang}${pathWithoutLang}`,
	}))

	links.push({
		tagName: 'link' as const,
		rel: 'alternate',
		hrefLang: 'x-default',
		href: `${base}/${DEFAULT_LANGUAGE}${pathWithoutLang}`,
	})

	return links
}
